import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { FaPlus, FaEllipsisV, FaGlobe, FaGlobeAmericas, FaArrowLeft, FaTimes, FaChevronUp, FaChevronDown, FaDatabase, FaBookmark, FaComment, FaTable, FaSave, FaTrash } from 'react-icons/fa';
import "./sidebarmenu.css";
import logo from '../../assets/img/Iqvia-logo-color.svg.png'; 
import graficasBI from '../../assets/img/iqviabanner.png';
import { useAuthenticator } from '@aws-amplify/ui-react';
import logoChat from '../../assets/img/logo-chat.png';

import { fromCognitoIdentityPool } from "@aws-sdk/credential-providers";
import { fetchAuthSession } from 'aws-amplify/auth';
import { SignatureV4 } from "@aws-sdk/signature-v4";
import { HttpRequest } from "@aws-sdk/protocol-http";
import { Sha256 } from "@aws-crypto/sha256-browser";

import config from '../../amplifyconfiguration.json';

const UsuariosLink = process.env.REACT_APP_USUARIOS_LINK;
const baseURL = process.env.REACT_APP_BASE_URL;
const endpoint_get = '/get_conversations_user';
const endpoint_add = '/add_conversation';
const endpoint_delete = '/delete_conversation';
// Nuevos endpoints para consultas guardadas
const endpoint_get_saved_consults = '/get_saved_consults';
const endpoint_add_saved_consult = '/add_saved_consult';
const endpoint_delete_consult = '/delete_consult';

// Componente para el elemento de una conversación
const ConversationLink = ({ id_conversation, description, handleDelete, type_name, isActive }) => {
  const navigate = useNavigate();

  const handleConversationClick = () => {
    const path = type_name === "geo" ? `/geo/${id_conversation}` : `/chat/${id_conversation}`;
    navigate(path);
  };

  // Función simple para manejar la eliminación
  const handleDeleteClick = (e) => {
    e.stopPropagation(); // Evita que se navegue a la conversación
    handleDelete(id_conversation, description);
  };

  return (
    <div
      className={`sidebar-link ${isActive ? 'active' : ''}`}
      onClick={handleConversationClick}
    >
      <img src={logoChat} alt="Logo Chat" className="logo-chat" />
      <span className="conversation-title">{description}</span>

      {/* Botón de tres puntos que solo se muestra cuando está activo */}
      <button
        className="delete-conversation-btn"
        onClick={handleDeleteClick}
        title="Opciones"
      >
        <FaEllipsisV size={14} />
      </button>
    </div>
  );
};

// Componente para el encabezado de acordeón
const AccordionHeader = ({ title, icon, isOpen, onClick }) => {
  return (
    <div className="accordion-header" onClick={onClick}>
      {icon}
      <span className="accordion-title">{title}</span>
      {isOpen ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
    </div>
  );
};

// Componente modificado para un elemento de consulta guardada
const SavedQueryItem = ({ id_saved, consult, onSelect, onDelete }) => {
  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (typeof onSelect === 'function') {
      onSelect(consult);
    }
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (typeof onDelete === 'function') {
      onDelete(id_saved, consult);
    }
  };

  return (
    <div
      className="sidebar-link saved-query"
      onClick={handleClick}
    >
      <FaBookmark size={14} className="query-icon" />
      <span className="query-title">{consult.length > 40 ? consult.substring(0, 40) + '...' : consult}</span>

      {/* Botón de tres puntos para eliminar */}
      <button
        className="delete-conversation-btn"
        onClick={handleDeleteClick}
        title="Eliminar consulta"
      >
        <FaEllipsisV size={14} />
      </button>
    </div>
  );
};

// Componente para un elemento del diccionario de datos
const DataDictionaryItem = ({ tableName, description, columns, isOpen, toggleTable }) => {
  return (
    <div className="data-dictionary-item">
      <div className="table-header" onClick={() => toggleTable(tableName)}>
        <FaTable size={14} className="table-icon" />
        <span className="table-name">{tableName}</span>
        {isOpen ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
      </div>

      {isOpen && (
        <div className="table-columns custom-scrollbar">
          <div className="table-description">{description}</div>
          <div className="columns-container">
            {columns.map((column, index) => (
              <div key={index} className="column-info">
                <span className="column-name">{column.name}</span>
                <span className="column-type">{column.type}</span>
                {column.description && (
                  <span className="column-description">{column.description}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const SidebarMenu = ({ isSidebarVisible, toggleSidebar, setMessage, messageInputRef }) => {
  const { user } = useAuthenticator((context) => [context.user]);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [selectedConversationName, setSelectedConversationName] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Estados para consultas guardadas
  const [savedQueries, setSavedQueries] = useState([]);
  const [isLoadingQueries, setIsLoadingQueries] = useState(true);
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [showQueryModal, setShowQueryModal] = useState(false);
  const [modalType, setModalType] = useState('conversation'); // 'conversation' o 'query'

  // Estados para los acordeones
  const [openAccordion, setOpenAccordion] = useState('conversations');
  const [openTable, setOpenTable] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  // Obtener la conversación activa de la URL
  const currentPath = location.pathname;
  const activeChatId = currentPath.includes('/chat/')
    ? currentPath.split('/chat/')[1]
    : (currentPath.includes('/geo/') ? currentPath.split('/geo/')[1] : null);

  // 🗂️ DICCIONARIO DE DATOS - Organizado por tipo de agente
  const dataDictionary = {
    subnacional: [
      {
        tableName: "public.gps_full",
        description: "Prescription and Dispensation Data by Physician - Contains detailed physician information and prescription/dispensation metrics",
        agentType: "Subnacional",
        columns: [
      { name: "ADDRESS", type: "char", description: "Physician's address" },
      { name: "app1", type: "char", description: "Application form code level 1" },
      { name: "app1_desc", type: "char", description: "Application form description level 1" },
      { name: "app2", type: "char", description: "Application form code level 2" },
      { name: "app2_desc", type: "char", description: "Application form description level 2" },
      { name: "app3", type: "char", description: "Application form code level 3" },
      { name: "app3_desc", type: "char", description: "Application form description level 3" },
      { name: "atc1", type: "char", description: "ATC code level 1" },
      { name: "atc1_desc", type: "char", description: "ATC description level 1" },
      { name: "atc2", type: "char", description: "ATC code level 2" },
      { name: "atc2_desc", type: "char", description: "ATC description level 2" },
      { name: "atc3", type: "char", description: "ATC code level 3" },
      { name: "atc3_desc", type: "char", description: "ATC description level 3" },
      { name: "atc4", type: "char", description: "ATC code level 4" },
      { name: "atc4_desc", type: "char", description: "ATC description level 4" },
      { name: "BIRTHDATE", type: "char", description: "Physician's birth date" },
      { name: "BIRTHDATE_DAY", type: "numeric", description: "Physician's birth day" },
      { name: "BIRTHDATE_MONTH", type: "numeric", description: "Physician's birth month" },
      { name: "BIRTHDATE_YEAR", type: "numeric", description: "Physician's birth year" },
      { name: "doctor_BRICK", type: "numeric", description: "IQVIA unique brick code for physician location" },
      { name: "doctor_brick_desc", type: "char", description: "Brick name/description" },
      { name: "CEDULA_SEP", type: "char", description: "Professional license number" },
      { name: "corp_desc", type: "char", description: "Corporation name that owns the laboratory producing the SKU" },
      { name: "corporation_code", type: "numeric", description: "Corporation code of the laboratory producing the SKU" },
      { name: "doctor_id", type: "numeric", description: "IQVIA unique physician code" },
      { name: "dsg_fact", type: "numeric", description: "Factor to convert pack units to dose units (divide by 100 to apply)" },
      { name: "dx", type: "numeric", description: "Number of dispensations" },
      { name: "fcc", type: "numeric", description: "IQVIA unique pack code" },
      { name: "FIRST_NAME", type: "char", description: "Physician's first name" },
      { name: "GENDER", type: "char", description: "Physician's gender (F=Female, M=Male)" },
      { name: "gene_cod", type: "char", description: "Pack type (G=Generic, M=Brand)" },
      { name: "HONORARIOS", type: "char", description: "Physician's fees" },
      { name: "ID_ONEKEY", type: "char", description: "OneKey ID" },
      { name: "ID_PBS", type: "numeric", description: "PBS ID" },
      { name: "lab_cod", type: "numeric", description: "Laboratory code that manufactures the SKU" },
      { name: "labdesc", type: "char", description: "Laboratory description/name that manufactures the SKU" },
      { name: "LAST_NAME", type: "char", description: "Physician's last name" },
      { name: "market", type: "char", description: "Market type (E=Ethical, P=OTC/Popular)" },
      { name: "molecula", type: "char", description: "Molecule name associated with the SKU" },
      { name: "nec1", type: "char", description: "NEC code level 1 (used for non-pharma or hybrid packs)" },
      { name: "nec2", type: "char", description: "NEC code level 2 (used for non-pharma or hybrid packs)" },
      { name: "nec3", type: "char", description: "NEC code level 3 (used for non-pharma or hybrid packs)" },
      { name: "nec4", type: "char", description: "NEC code level 4" },
      { name: "otc1_desc", type: "char", description: "OTC ATC description level 1" },
      { name: "otc2_desc", type: "char", description: "OTC ATC description level 2" },
      { name: "otc3_desc", type: "char", description: "OTC ATC description level 3" },
      { name: "otc4_desc", type: "char", description: "OTC ATC description level 4" },
      { name: "pack_cod", type: "numeric", description: "Unique pack code seen by client" },
      { name: "pack_desc", type: "char", description: "Pack or SKU description" },
      { name: "pack_lch", type: "char", description: "SKU launch date (format YYYY-MM-DD)" },
      { name: "period", type: "numeric", description: "Period in YYYYMM format" },
      { name: "prod_lch", type: "char", description: "Product or brand launch date (format YYYY-MM-DD)" },
      { name: "product_cod", type: "numeric", description: "Product or brand code" },
      { name: "product_desc", type: "char", description: "Product or brand description" },
      { name: "RANGO_PAT", type: "char", description: "Patient range served by physician" },
      { name: "doctor_region_code", type: "numeric", description: "IQVIA region code" },
      { name: "doctor_region_description", type: "char", description: "IQVIA region description" },
      { name: "rx", type: "numeric", description: "Number of prescriptions" },
      { name: "segment_flag", type: "char", description: "Market segment (PH=Pure Pharma, BH=Hybrid, CH=Consumer Product)" },
      { name: "SPECIALTY_ID_1", type: "char", description: "Physician's primary specialty" },
      { name: "SPECIALTY_ID_2", type: "char", description: "Physician's secondary specialty" },
      { name: "doctor_state_code", type: "numeric", description: "State code (numeric)" },
      { name: "doctor_state_desc", type: "char", description: "State name" },
      { name: "std_fact", type: "numeric", description: "Factor to convert pack units to standard units (divide by 100 to apply)" },
      { name: "stgh_mea", type: "char", description: "Metric for measuring active substance (e.g., MG, G, %)" },
      { name: "stgh_val", type: "numeric", description: "Amount of active substance measured in stgh_mea metric" },
      { name: "TIPO_CONSULTORIO", type: "char", description: "Office type" },
      { name: "VISITS_NUMBER", type: "numeric", description: "Number of physician visits" },
      { name: "dispensed_brick_code", type: "numeric", description: "Dispensation brick code" },
      { name: "dispensed_brick_desc", type: "char", description: "Dispensation brick description" },
      { name: "dispensed_region_code", type: "numeric", description: "Dispensation region code" },
      { name: "dispensed_region_description", type: "char", description: "Dispensation region description" },
      { name: "dispensed_state_code", type: "numeric", description: "Dispensation state code" },
      { name: "dispensed_state_desc", type: "char", description: "Dispensation state description" }
    ]
  },
  {
    tableName: "public.ddd_full",
    description: "Sales Data by Geography - Market data with units sold and monetary values at geographic granularity",
    columns: [
      { name: "app1", type: "char", description: "Application form code level 1" },
      { name: "app1_desc", type: "char", description: "Application form description level 1" },
      { name: "app2", type: "char", description: "Application form code level 2" },
      { name: "app2_desc", type: "char", description: "Application form description level 2" },
      { name: "app3", type: "char", description: "Application form code level 3" },
      { name: "app3_desc", type: "char", description: "Application form description level 3" },
      { name: "atc1", type: "char", description: "ATC code level 1" },
      { name: "atc1_desc", type: "char", description: "ATC description level 1" },
      { name: "atc2", type: "char", description: "ATC code level 2" },
      { name: "atc2_desc", type: "char", description: "ATC description level 2" },
      { name: "atc3", type: "char", description: "ATC code level 3" },
      { name: "atc3_desc", type: "char", description: "ATC description level 3" },
      { name: "atc4", type: "char", description: "ATC code level 4" },
      { name: "atc4_desc", type: "char", description: "ATC description level 4" },
      { name: "brick_code", type: "numeric", description: "IQVIA unique brick code" },
      { name: "brick_desc", type: "char", description: "Brick name/description" },
      { name: "corp_desc", type: "char", description: "Corporation name that owns the laboratory producing the SKU" },
      { name: "corporation_code", type: "numeric", description: "Corporation code of the laboratory producing the SKU" },
      { name: "dsg_fact", type: "numeric", description: "Factor to convert pack units to dose units (divide by 100 to apply)" },
      { name: "fcc", type: "numeric", description: "IQVIA unique pack code" },
      { name: "gene_cod", type: "char", description: "Pack type (G=Generic, M=Brand)" },
      { name: "lab_cod", type: "numeric", description: "Laboratory code that manufactures the SKU" },
      { name: "labdesc", type: "char", description: "Laboratory description/name that manufactures the SKU" },
      { name: "market", type: "char", description: "Market type (E=Ethical, P=OTC/Popular)" },
      { name: "molecula", type: "char", description: "Molecule name associated with the SKU" },
      { name: "nec1", type: "char", description: "NEC code level 1" },
      { name: "nec2", type: "char", description: "NEC code level 2 (used for non-pharma or hybrid packs)" },
      { name: "nec3", type: "char", description: "NEC code level 3 (used for non-pharma or hybrid packs)" },
      { name: "nec4", type: "char", description: "NEC code level 4" },
      { name: "otc1_desc", type: "char", description: "OTC ATC description level 1" },
      { name: "otc2_desc", type: "char", description: "OTC ATC description level 2" },
      { name: "otc3_desc", type: "char", description: "OTC ATC description level 3" },
      { name: "otc4_desc", type: "char", description: "OTC ATC description level 4" },
      { name: "pack_cod", type: "numeric", description: "Unique pack code seen by client" },
      { name: "pack_desc", type: "char", description: "Pack or SKU description" },
      { name: "pack_lch", type: "char", description: "SKU launch date (format YYYY-MM-DD)" },
      { name: "period", type: "numeric", description: "Period in YYYYMM format" },
      { name: "prod_lch", type: "char", description: "Product or brand launch date (format YYYY-MM-DD)" },
      { name: "product_cod", type: "numeric", description: "Product or brand code" },
      { name: "product_desc", type: "char", description: "Product or brand description" },
      { name: "region_code", type: "numeric", description: "IQVIA region code" },
      { name: "region_description", type: "char", description: "IQVIA region description" },
      { name: "segment_flag", type: "char", description: "Market segment (PH=Pure Pharma, BH=Hybrid, CH=Consumer Product)" },
      { name: "state_code", type: "numeric", description: "State code (numeric)" },
      { name: "state_desc", type: "char", description: "State name" },
      { name: "std_fact", type: "numeric", description: "Factor to convert pack units to standard units (divide by 100 to apply)" },
      { name: "stgh_mea", type: "char", description: "Metric for measuring active substance (e.g., MG, G, %)" },
      { name: "stgh_val", type: "numeric", description: "Amount of active substance measured in stgh_mea metric" },
      { name: "units", type: "numeric", description: "Units sold" },
      { name: "values", type: "numeric", description: "Monetary values sold" }
    ]
  }
],
nacional: [
  {
    tableName: "public.channel_data",
    description: "Channels and Promotional Strategy - Information about promotional channels, interactions, and spending",
    agentType: "Nacional",
    columns: [
      { name: "period", type: "numeric", description: "Period in YYYYMM format" },
      { name: "product_cod", type: "numeric", description: "Product or brand code" },
      { name: "product_desc", type: "char", description: "Product or brand description" },
      { name: "Herbal_Medicine", type: "char", description: "Herbal medicine indicator" },
      { name: "Channels", type: "char", description: "Communication channel used" },
      { name: "Channel_Detail", type: "char", description: "Detailed channel information" },
      { name: "Channel_Type", type: "char", description: "Type of channel" },
      { name: "Contact_Type", type: "char", description: "Type of contact made" },
      { name: "Contact_Quality", type: "char", description: "Quality of contact" },
      { name: "Current_Prescribing", type: "char", description: "Current prescribing status" },
      { name: "Future_Prescribing", type: "char", description: "Future prescribing intention" },
      { name: "NPS_Scale", type: "numeric", description: "Net Promoter Score (0-10)" },
      { name: "NPS_Categories", type: "char", description: "NPS category (Promoter/Passive/Detractor)" },
      { name: "Visit_Location", type: "char", description: "Location of visit" },
      { name: "Sample_Drop", type: "char", description: "Sample drop indicator" },
      { name: "Visual_Aid_Type", type: "char", description: "Type of visual aid used" },
      { name: "Products_Presented", type: "char", description: "Products presented during visit" },
      { name: "Product_Position", type: "char", description: "Position of product in presentation" },
      { name: "Visit_Duration_(mins)", type: "numeric", description: "Duration of visit in minutes" },
      { name: "Visit_Category", type: "char", description: "Category of visit" },
      { name: "Visit_Function", type: "char", description: "Function/purpose of visit" },
      { name: "Meeting_Type", type: "char", description: "Type of meeting" },
      { name: "Streaming_Video_Platform", type: "char", description: "Video platform used" },
      { name: "Continuing_Medical_Education", type: "char", description: "CME indicator" },
      { name: "total_spending", type: "numeric", description: "Total spending amount" },
      { name: "lab_cod", type: "numeric", description: "Laboratory code" },
      { name: "labdesc", type: "char", description: "Laboratory name" },
      { name: "atc1", type: "char", description: "ATC level 1 code" },
      { name: "atc2", type: "char", description: "ATC level 2 code" },
      { name: "atc3", type: "char", description: "ATC level 3 code" },
      { name: "atc4", type: "char", description: "ATC level 4 code" },
      { name: "nec1", type: "char", description: "NEC level 1 code" },
      { name: "nec2", type: "char", description: "NEC level 2 code" },
      { name: "nec3", type: "char", description: "NEC level 3 code" },
      { name: "nec4", type: "char", description: "NEC level 4 code" },
      { name: "molecula", type: "char", description: "Molecule name" },
      { name: "market", type: "char", description: "Market type (E=Ethical, P=OTC)" }
    ]
  },
  {
    tableName: "public.medical_data",
    description: "Medical Information and Diagnoses - Patient demographics, diagnoses, and prescription data",
    agentType: "Nacional",
    columns: [
      { name: "period", type: "numeric", description: "Period in YYYYMM format" },
      { name: "pack", type: "char", description: "Pack or SKU code" },
      { name: "product_cod", type: "numeric", description: "Product or brand code" },
      { name: "product_desc", type: "char", description: "Product or brand description" },
      { name: "diagnostico_3", type: "char", description: "Diagnosis level 3" },
      { name: "co_diagnostico_3", type: "char", description: "Co-diagnosis level 3" },
      { name: "grupo_edad_paciente", type: "char", description: "Patient age group" },
      { name: "sexo_del_paciente", type: "char", description: "Patient gender" },
      { name: "efecto_deseado_2", type: "char", description: "Desired effect level 2" },
      { name: "grupo_duracion", type: "char", description: "Treatment duration group" },
      { name: "grupo_tomas_diarias", type: "char", description: "Daily dose group" },
      { name: "tipo_de_tratamiento", type: "char", description: "Type of treatment" },
      { name: "grupo_de_dosis_por_toma", type: "char", description: "Dose per intake group" },
      { name: "rx", type: "numeric", description: "Number of prescriptions" },
      { name: "lab_cod", type: "numeric", description: "Laboratory code" },
      { name: "labdesc", type: "char", description: "Laboratory name" },
      { name: "atc1", type: "char", description: "ATC level 1 code" },
      { name: "atc2", type: "char", description: "ATC level 2 code" },
      { name: "atc3", type: "char", description: "ATC level 3 code" },
      { name: "atc4", type: "char", description: "ATC level 4 code" },
      { name: "nec1", type: "char", description: "NEC level 1 code" },
      { name: "nec2", type: "char", description: "NEC level 2 code" },
      { name: "nec3", type: "char", description: "NEC level 3 code" },
      { name: "nec4", type: "char", description: "NEC level 4 code" },
      { name: "molecula", type: "char", description: "Molecule name" },
      { name: "market", type: "char", description: "Market type" }
    ]
  },
  {
    tableName: "public.pm_data",
    description: "Pharmaceutical Market Data - Sales units and values by product",
    agentType: "Nacional",
    columns: [
      { name: "period", type: "numeric", description: "Period in YYYYMM format" },
      { name: "pack", type: "char", description: "Pack or SKU code" },
      { name: "product_cod", type: "numeric", description: "Product or brand code" },
      { name: "product_desc", type: "char", description: "Product or brand description" },
      { name: "unidades", type: "numeric", description: "Units sold" },
      { name: "valores", type: "numeric", description: "Monetary values sold" },
      { name: "lab_cod", type: "numeric", description: "Laboratory code" },
      { name: "labdesc", type: "char", description: "Laboratory name" },
      { name: "atc1", type: "char", description: "ATC level 1 code" },
      { name: "atc2", type: "char", description: "ATC level 2 code" },
      { name: "atc3", type: "char", description: "ATC level 3 code" },
      { name: "atc4", type: "char", description: "ATC level 4 code" },
      { name: "nec1", type: "char", description: "NEC level 1 code" },
      { name: "nec2", type: "char", description: "NEC level 2 code" },
      { name: "nec3", type: "char", description: "NEC level 3 code" },
      { name: "nec4", type: "char", description: "NEC level 4 code" },
      { name: "molecula", type: "char", description: "Molecule name" },
      { name: "market", type: "char", description: "Market type" },
      { name: "app1", type: "char", description: "Application form level 1" },
      { name: "app2", type: "char", description: "Application form level 2" },
      { name: "app3", type: "char", description: "Application form level 3" },
      { name: "std_fact", type: "numeric", description: "Standard unit factor" },
      { name: "dsg_fact", type: "numeric", description: "Dose factor" }
    ]
  },
  {
    tableName: "public.nps_data",
    description: "Net Promoter Score and Loyalty - Prescriptions, dispensations, and physician specialties",
    agentType: "Nacional",
    columns: [
      { name: "period", type: "numeric", description: "Period in YYYYMM format" },
      { name: "pack", type: "char", description: "Pack or SKU code" },
      { name: "product_cod", type: "numeric", description: "Product or brand code" },
      { name: "product_desc", type: "char", description: "Product or brand description" },
      { name: "SPECIALTY_CD", type: "char", description: "Physician specialty code" },
      { name: "rx", type: "numeric", description: "Number of prescriptions" },
      { name: "dx", type: "numeric", description: "Number of dispensations" },
      { name: "lab_cod", type: "numeric", description: "Laboratory code" },
      { name: "labdesc", type: "char", description: "Laboratory name" },
      { name: "atc1", type: "char", description: "ATC level 1 code" },
      { name: "atc2", type: "char", description: "ATC level 2 code" },
      { name: "atc3", type: "char", description: "ATC level 3 code" },
      { name: "atc4", type: "char", description: "ATC level 4 code" },
      { name: "nec1", type: "char", description: "NEC level 1 code" },
      { name: "nec2", type: "char", description: "NEC level 2 code" },
      { name: "nec3", type: "char", description: "NEC level 3 code" },
      { name: "nec4", type: "char", description: "NEC level 4 code" },
      { name: "molecula", type: "char", description: "Molecule name" },
      { name: "market", type: "char", description: "Market type" }
    ]
  }
]
};

  // Función para alternar entre acordeones
  const toggleAccordion = (accordionName) => {
    setOpenAccordion(openAccordion === accordionName ? null : accordionName);
  };

  // Función para alternar la visualización de tablas
  const toggleTable = (tableName) => {
    setOpenTable(openTable === tableName ? null : tableName);
  };

  // Función para seleccionar una consulta guardada
  const handleSelectQuery = (query) => {
     

    // 1. Intentar usar la función setMessage si está disponible
    if (typeof setMessage === 'function') {
      setMessage(query);
       
    }

    // 2. Emitir un evento personalizado como respaldo
    try {
      // Crear un evento personalizado con los datos de la consulta
      const customEvent = new CustomEvent('savedQuerySelected', {
        detail: { query: query },
        bubbles: true,
        cancelable: true
      });

      // Disparar el evento desde el documento
      document.dispatchEvent(customEvent);
       

      // 3. Como último recurso, intentar actualizar directamente el textarea
      setTimeout(() => {
        const textareaElement = document.querySelector('.message-input');
        if (textareaElement) {
          textareaElement.value = query;

          // Disparar un evento de input para activar los listeners de React
          const inputEvent = new Event('input', { bubbles: true });
          textareaElement.dispatchEvent(inputEvent);

          // Ajustar la altura y dar foco
          textareaElement.style.height = 'auto';
          textareaElement.style.height = (textareaElement.scrollHeight) + 'px';
          textareaElement.focus();

           
        } else {
          console.warn("Textarea no encontrado en el DOM");
        }
      }, 100); // Pequeño retraso para asegurar que el DOM esté actualizado
    } catch (error) {
      console.error("Error al manejar la consulta guardada:", error);
    }

    // Opcional: cerrar el sidebar en versión móvil
    if (window.innerWidth < 768 && toggleSidebar) {
      toggleSidebar();
    }
  };

  const createAuthenticatedRequest = async (config) => {
    const authToken = (await fetchAuthSession()).tokens?.idToken?.toString();
    const my_login = 'cognito-idp.' + config.aws_project_region + '.amazonaws.com/' + config.aws_user_pools_id;

    const credentials = fromCognitoIdentityPool({
      clientConfig: { region: config.aws_project_region },
      identityPoolId: config.aws_cognito_identity_pool_id,
      logins: { [my_login]: authToken }
    });

    const signer = new SignatureV4({
      credentials: credentials,
      region: config.aws_project_region,
      service: 'execute-api',
      sha256: Sha256
    });

    return async (endpoint, method = 'GET', body = null) => {
      const url = new URL(endpoint);

      const request = new HttpRequest({
        hostname: url.hostname,
        path: url.pathname + url.search,
        method: method,
        headers: {
          'Content-Type': 'application/json',
          host: url.hostname
        },
        body: body ? JSON.stringify(body) : undefined
      });

      const signedRequest = await signer.sign(request);

      const fetchOptions = {
        method: signedRequest.method,
        headers: signedRequest.headers,
        body: signedRequest.body
      };

      const response = await fetch(url.toString(), fetchOptions);
      return response.json();
    };
  };

  // Función para obtener consultas guardadas
  const fetchSavedQueries = async () => {
    if (user) {
      try {
        setIsLoadingQueries(true);
        const authenticatedRequest = await createAuthenticatedRequest(config);
        const url = `${baseURL}${endpoint_get_saved_consults}?id_user=${encodeURIComponent(user.username)}`;

        const response = await authenticatedRequest(url, 'GET', null);

        if (response && response.saved_consults) {
          // Ordenar consultas por fecha
          const sortedQueries = [...response.saved_consults].sort((a, b) => {
            return new Date(b.created_at) - new Date(a.created_at);
          });

          setSavedQueries(sortedQueries);
        } else {
          console.warn('No saved consults data in response:', response);
          setSavedQueries([]);
        }
      } catch (error) {
        console.error('Error fetching saved consults:', error);
        setSavedQueries([]);
      } finally {
        setIsLoadingQueries(false);
      }
    }
  };

  // Función para guardar una consulta actual
  const saveCurrentQuery = async (query) => {
    if (!user || !query) return;

    try {
      const authenticatedRequest = await createAuthenticatedRequest(config);
      const url = `${baseURL}${endpoint_add_saved_consult}`;

      const response = await authenticatedRequest(
        url,
        'POST',
        {
          id_user: user.username,
          consult: query
        }
      );

      if (response && response.id_saved) {
        // Actualizar la lista de consultas guardadas
        fetchSavedQueries();

        // Opcional: mostrar confirmación
         
      } else {
        console.error('Error al guardar la consulta:', response);
      }
    } catch (error) {
      console.error('Error saving query:', error);
    }
  };

  useEffect(() => {
    const fetchConversations = async () => {
      if (user) {
        try {
          setIsLoading(true);
          const authenticatedRequest = await createAuthenticatedRequest(config);
          const url = `${baseURL}${endpoint_get}?id_user=${encodeURIComponent(user.username)}`;

          const response = await authenticatedRequest(url, 'GET', null);

          if (response && response.conversations) {
            // Ordenar conversaciones por fecha
            const sortedConversations = [...response.conversations].sort((a, b) => {
              return b.id_conversation - a.id_conversation;
            });

            setConversations(sortedConversations);
          } else {
            console.warn('No conversations data in response:', response);
            setConversations([]);
          }
        } catch (error) {
          console.error('Error completo:', error);
          setConversations([]);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchConversations();
    fetchSavedQueries(); // Cargar las consultas guardadas
  }, [user]);

  // Función para manejar la eliminación de una consulta guardada
  const handleDeleteQuery = (id_saved, consult) => {
    setSelectedQuery(id_saved);
    setSelectedConversationName(consult.substring(0, 30) + (consult.length > 30 ? '...' : ''));
    setModalType('query');
    setShowModal(true);
  };

  // Función para confirmar la eliminación de una consulta guardada
  const deleteQuery = async () => {
    try {
      const authenticatedRequest = await createAuthenticatedRequest(config);
      const url = `${baseURL}${endpoint_delete_consult}`;

      const response = await authenticatedRequest(
        url,
        'DELETE',
        {
          id_saved: selectedQuery
        }
      );

      if (response) {
        // Actualizar el estado local
        setSavedQueries(prevQueries =>
          prevQueries.filter(query => query.id_saved !== selectedQuery)
        );

        // Cerrar el modal
        setShowModal(false);
        setSelectedQuery(null);
        setSelectedConversationName("");
      }
    } catch (error) {
      console.error('Error deleting saved query:', error);
    }
  };

  const addConversation = async () => {
    try {
      const authenticatedRequest = await createAuthenticatedRequest(config);
      const url = `${baseURL}${endpoint_add}`;

      const response = await authenticatedRequest(
        url,
        'POST',
        {
          id_user: user.username,
          type_name: "text"
        }
      );

      if (response && response.id_conversation) {
        navigate(`/chat/${response.id_conversation}`);

        // Actualizar la lista de conversaciones
        const newConversation = {
          id_conversation: response.id_conversation,
          description: response.description || "Nueva consulta",
          type_name: "text"
        };

        setConversations(prev => [newConversation, ...prev]);
      } else {
        console.error('No se recibió id_conversation en la respuesta:', response);
      }
    } catch (error) {
      console.error('Error creating new conversation:', error);
    }
  };

  // Agregar este useEffect después de los otros useEffects existentes
  useEffect(() => {
    // Listener para actualizar las consultas guardadas cuando se guarda una nueva
    const handleQuerySaved = () => {
       
      fetchSavedQueries(); // Refrescar la lista desde el servidor
      setOpenAccordion('savedQueries'); // Abrir el acordeón de consultas guardadas
    };

    // Registrar el listener
    document.addEventListener('querySaved', handleQuerySaved);

    // Limpieza al desmontar
    return () => {
      document.removeEventListener('querySaved', handleQuerySaved);
    };
  }, []);

  const handleDelete = (id_conversation, description) => {
    setSelectedConversation(id_conversation);
    setSelectedConversationName(description || "esta conversación");
    setModalType('conversation');
    setShowModal(true);
  };

  const deleteConversation = async () => {
    try {
      const authenticatedRequest = await createAuthenticatedRequest(config);
      const url = `${baseURL}${endpoint_delete}`;

      const response = await authenticatedRequest(
        url,
        'DELETE',
        {
          id_conversation: selectedConversation
        }
      );

      if (response) {
        // Actualizar el estado local
        setConversations(prevConversations =>
          prevConversations.filter(convo => convo.id_conversation !== selectedConversation)
        );

        // Cerrar el modal
        setShowModal(false);
        setSelectedConversation(null);
        setSelectedConversationName("");

        // Si estábamos viendo la conversación eliminada, ir a la página principal
        if (activeChatId === selectedConversation) {
          navigate('/');
        }
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
    }
  };

  // Función para manejar la acción del modal de confirmación
  const handleModalAction = () => {
    if (modalType === 'conversation') {
      deleteConversation();
    } else {
      deleteQuery();
    }
  };

  return (
    <div className={`sidebar ${isSidebarVisible ? 'visible' : 'hidden'}`}>
      <div className="sidebar-section logo-section">
        <Link to="/">
          <img src={logo} alt="Logo" className="sidebar-logo" />
        </Link>
      </div>

      <div className="accordion-sections-container">
        {/* Acordeón de Conversaciones */}
        <div className="accordion-section">
          <AccordionHeader
            title="Conversaciones"
            icon={<FaComment size={16} className="accordion-icon" />}
            isOpen={openAccordion === 'conversations'}
            onClick={() => toggleAccordion('conversations')}
          />

          {openAccordion === 'conversations' && (
            <div className="accordion-content">
              <div className="sidebar-link new-chat" onClick={addConversation}>
                <FaPlus size={14} style={{ marginRight: '8px' }} /> Nueva consulta
              </div>

              {isLoading ? (
                <div className="sidebar-loading">
                  <div className="sidebar-loader"></div>
                  <span>Cargando...</span>
                </div>
              ) : (
                conversations.length === 0 ? (
                  <div className="no-conversations">
                    <p>No hay conversaciones</p>
                    <p className="no-conversations-sub">Comienza una nueva consulta</p>
                  </div>
                ) : (
                  conversations.map((conversation) => (
                    <ConversationLink
                      key={conversation.id_conversation}
                      id_conversation={conversation.id_conversation}
                      type_name={conversation.type_name}
                      description={conversation.description}
                      handleDelete={handleDelete}
                      isActive={conversation.id_conversation === activeChatId}
                    />
                  ))
                )
              )}
            </div>
          )}
        </div>

        {/* Acordeón de Consultas Guardadas */}
        <div className="accordion-section">
          <AccordionHeader
            title="Consultas Guardadas"
            icon={<FaBookmark size={16} className="accordion-icon" />}
            isOpen={openAccordion === 'savedQueries'}
            onClick={() => toggleAccordion('savedQueries')}
          />

          {openAccordion === 'savedQueries' && (
            <div className="accordion-content">
              {isLoadingQueries ? (
                <div className="sidebar-loading">
                  <div className="sidebar-loader"></div>
                  <span>Cargando consultas...</span>
                </div>
              ) : savedQueries.length === 0 ? (
                <div className="no-items">
                  <p>No hay consultas guardadas</p>
                </div>
              ) : (
                savedQueries.map((queryItem) => (
                  <SavedQueryItem
                    key={queryItem.id_saved}
                    id_saved={queryItem.id_saved}
                    consult={queryItem.consult}
                    onSelect={handleSelectQuery}
                    onDelete={handleDeleteQuery}
                  />
                ))
              )}
            </div>
          )}
        </div>

        {/* Acordeón de Diccionario de Datos */}
        <div className="accordion-section">
          <AccordionHeader
            title="Diccionario de Datos"
            icon={<FaDatabase size={16} className="accordion-icon" />}
            isOpen={openAccordion === 'dataDictionary'}
            onClick={() => toggleAccordion('dataDictionary')}
          />

          {openAccordion === 'dataDictionary' && (
            <div className="accordion-content">
              {/* 🌍 AGENTE SUBNACIONAL */}
              <div className="agent-section">
                <div className="agent-header subnacional">
                  <FaGlobe size={14} />
                  <span>Agente Subnacional</span>
                  <span className="table-count">{dataDictionary.subnacional.length} tablas</span>
                </div>
                {dataDictionary.subnacional.map((table) => (
                  <DataDictionaryItem
                    key={table.tableName}
                    tableName={table.tableName}
                    description={table.description}
                    columns={table.columns}
                    isOpen={openTable === table.tableName}
                    toggleTable={toggleTable}
                  />
                ))}
              </div>

              {/* 🌎 AGENTE NACIONAL */}
              <div className="agent-section">
                <div className="agent-header nacional">
                  <FaGlobeAmericas size={14} />
                  <span>Agente Nacional</span>
                  <span className="table-count">{dataDictionary.nacional.length} tablas</span>
                </div>
                {dataDictionary.nacional.map((table) => (
                  <DataDictionaryItem
                    key={table.tableName}
                    tableName={table.tableName}
                    description={table.description}
                    columns={table.columns}
                    isOpen={openTable === table.tableName}
                    toggleTable={toggleTable}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

   

      {/* Modal de confirmación */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <p>
              {modalType === 'conversation'
                ? '¿Desea eliminar la conversación?'
                : '¿Desea eliminar esta consulta guardada?'}
            </p>
            <div className="modal-buttons">
              <button onClick={handleModalAction}>Aceptar</button>
              <button onClick={() => setShowModal(false)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SidebarMenu;